// Shared by the page and its service worker. This records account ownership,
// never coordinates or credentials, so a push cannot reveal a previous user's
// alert on a shared device after sign-out or account switching.
const DB = 'mylocation-place-alerts';
const STORE = 'delivery';

/** @param {'readonly'|'readwrite'} mode @param {(store:IDBObjectStore)=>IDBRequest|(()=>unknown)} operation */
async function deliveryRecord(mode, operation) {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB, 1);
		request.onupgradeneeded = () => request.result.createObjectStore(STORE);
		request.onerror = () => reject(request.error);
		request.onsuccess = () => {
			const db = request.result;
			const tx = db.transaction(STORE, mode);
			const result = operation(tx.objectStore(STORE));
			tx.oncomplete = () => { db.close(); resolve(typeof result === 'function' ? result() : result.result); };
			tx.onerror = () => { db.close(); reject(tx.error); };
			tx.onabort = () => { db.close(); reject(tx.error); };
		};
	});
}

/** @param {string} uid */
export async function setPushOwner(uid) { await deliveryRecord('readwrite', store => store.put(uid, 'owner')); }
export async function getPushOwner() { return await deliveryRecord('readonly', store => store.get('owner')); }

/** Keep at most 100 recent delivery IDs for one account. No message text is stored.
 * @param {Array<{id:string,at:number}>} previous @param {string} eventId @param {number} now */
export function rememberPushEvent(previous, eventId, now) {
	const recent = previous.filter(item => item.at > now - 86400000 && item.at <= now);
	if (recent.some(item => item.id === eventId)) return null;
	return [...recent.slice(-99), { id: eventId, at: now }];
}

/** The owner check and event claim share one write transaction, so concurrent
 * retries and sign-out cannot both claim a notification for the wrong account.
 * @param {any} payload @param {number} now */
export async function claimPlaceAlertPush(payload, now = Date.now()) {
	return await deliveryRecord('readwrite', store => {
		let claimed = false;
		const owner = store.get('owner');
		owner.onsuccess = () => {
			if (!validPlaceAlertPush(payload, owner.result)) return;
			const history = store.get('events');
			history.onsuccess = () => {
				const previous = history.result?.owner === owner.result ? history.result.items : [];
				const items = rememberPushEvent(Array.isArray(previous) ? previous : [], payload.eventId, now);
				if (!items) return;
				store.put({ owner: owner.result, items }, 'events');
				claimed = true;
			};
		};
		return () => claimed;
	});
}

/** Only known payloads for the current account can produce system notifications.
 * @param {any} payload @param {unknown} owner */
export function validPlaceAlertPush(payload, owner) {
	return !!owner && payload?.type === 'place_alert' && payload.recipientUid === owner &&
		typeof payload.ruleId === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(payload.ruleId) &&
		typeof payload.subjectUid === 'string' && payload.subjectUid.length > 0 && payload.subjectUid.length <= 128 &&
		typeof payload.eventId === 'string' && payload.eventId.length > 0 && payload.eventId.length <= 256 &&
		['arrive', 'leave', 'request', 'approved', 'declined', 'revoked'].includes(payload.event) &&
		typeof payload.title === 'string' && payload.title.length <= 180 &&
		typeof payload.body === 'string' && payload.body.length <= 500;
}
